import {
  faExternalLinkAlt,
  faEye,
  faSort,
  faSortDown,
  faSortUp,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Table } from 'react-bootstrap';
import { useGlobalFilter, useSortBy, useTable } from 'react-table';
import GlobalTableFilter from '../GlobalTableFilter';
import LoadingOverlay from '../LoadingOverlay';
import PropertyGroup from './PropertyGroup';
import PropertyGroupForm, {
  PropertyGroupFormValidator
} from './PropertyGroupForm';

function PropertyGroupsTable({
  data,
  changeSelectedPropertyGroup,
  deletePropertyGroup
}) {
  const [showDeletedPropertyGroups, setShowDeletedPropertyGroups] =
    useState(false);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name'
      },
      {
        id: 'actions',
        sortable: false,
        filterable: false,
        Header: (tableInstance) => (
          <div className='text-right'>
            <Button
              type='button'
              variant='link'
              className={
                tableInstance.state.showDeletedPropertyGroups
                  ? 'text-primary'
                  : 'text-dark'
              }
              onClick={() => {
                setShowDeletedPropertyGroups(
                  !tableInstance.state.showDeletedPropertyGroups
                );
              }}
            >
              <FontAwesomeIcon icon={faEye} />
            </Button>
          </div>
        ),
        Cell: ({ row }) => {
          return (
            <div className='text-right'>
              {/* Select property group */}
              <Button
                type='button'
                variant='link'
                className='text-primary'
                onClick={() => changeSelectedPropertyGroup(row.original.id)}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Button>

              {/* Delete property group */}
              {row.original.deleted_at === null &&
              row.original.userProperty.can_edit ? (
                <Button
                  type='button'
                  variant='link'
                  className='text-danger'
                  onClick={() => deletePropertyGroup(row.original.id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              ) : null}
            </div>
          );
        }
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            showDeletedPropertyGroups: showDeletedPropertyGroups
          }),
          [state, showDeletedPropertyGroups]
        );
      }
    },
    useGlobalFilter,
    useSortBy
  );

  return (
    <Fragment>
      <GlobalTableFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
        style={{
          marginBottom: '0.5rem',
          maxWidth: 500
        }}
      />
      <Table responsive {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                return (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {column.canSort ? (
                      <span className='px-2'>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <FontAwesomeIcon icon={faSortDown} />
                          ) : (
                            <FontAwesomeIcon icon={faSortUp} />
                          )
                        ) : (
                          <FontAwesomeIcon icon={faSort} />
                        )}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);

            if (showDeletedPropertyGroups || row.original.deleted_at === null) {
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    );
                  })}
                </tr>
              );
            }
          })}
        </tbody>
      </Table>
    </Fragment>
  );
}

function PropertyGroups(props) {
  // Data
  const [loadingPropertyGroups, setLoadingPropertyGroups] = useState(false);
  const [propertyGroups, setPropertyGroups] = useState([]);

  const [loadingPropertyRoles, setLoadingPropertyRoles] = useState(false);
  const [propertyRoles, setPropertyRoles] = useState([]);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);

  // Display options
  const [showAddPropertyGroupModal, setShowAddPropertyGroupModal] =
    useState(false);

  /**
   * Load the following when the bearer token is updated:
   *  - property groups
   *  - property roles
   *  - users (Used by propery group permissions)
   */
  useEffect(() => {
    if (!props.selectedDatabase || !props.bearerToken) return;
    // Property Groups
    setLoadingPropertyGroups(true);
    fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups?append[]=userProperty`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          SeasonalDatabase: props.selectedDatabase,
          Authorization: 'Bearer ' + props.bearerToken
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setPropertyGroups(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setPropertyGroups([]);
      })
      .finally(() => {
        setLoadingPropertyGroups(false);
      });

    // Property roles
    setLoadingPropertyRoles(true);
    fetch(`${process.env.REACT_APP_PROPERTIES_API_URL}api/property-roles`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + props.bearerToken,
        SeasonalDatabase: props.selectedDatabase
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setPropertyRoles(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setPropertyRoles([]);
      })
      .finally(() => {
        setLoadingPropertyRoles(false);
      });

    // Users
    setLoadingUsers(true);
    fetch(`${process.env.REACT_APP_AUTH_API_URL}api/users`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + props.bearerToken,
        service: props.authService
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setUsers(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setUsers([]);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, [props.bearerToken, props.selectedDatabase]);

  /**
   * Overwrites a property groups in the property groups state array.
   *
   * @param {object} propertyGroup
   */
  const updatePropertyGroup = (propertyGroup) => {
    const newPropertyGroups = [...propertyGroups];
    newPropertyGroups[
      newPropertyGroups.findIndex((p) => p.id === propertyGroup.id)
    ] = propertyGroup;
    setPropertyGroups(newPropertyGroups);
  };

  /**
   * Sends an http request to the properties API to delete the property
   * group and removes it from the property groups state array.
   *
   * @param {int} propertyGroupId
   */
  const deletePropertyGroup = (propertyGroupId) => {
    setLoadingPropertyGroups(true);
    fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${propertyGroupId}?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          /*
           * Property groups use soft deletion, so update the property group
           * in the property groups state array with the new deleted_at field.
           */
          updatePropertyGroup(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
      })
      .finally(() => {
        setLoadingPropertyGroups(false);
      });
  };

  const addPropertyGroup = (propertyGroup) => {
    const newPropertyGroups = [...propertyGroups];
    newPropertyGroups.push(propertyGroup);
    setPropertyGroups(newPropertyGroups);
  };

  /*
   * Render
   */
  // Check if anything is loading
  if (loadingPropertyGroups || loadingPropertyRoles || loadingUsers) {
    return (
      <div className='h-100'>
        <LoadingOverlay hideOpacity />
      </div>
    );
  } else {
    const selectedPropertyGroup =
      propertyGroups[
        propertyGroups.findIndex(
          (element) => element.id === props.selectedPropertyGroupId
        )
      ];

    // Check if a valid property has been selected
    if (selectedPropertyGroup) {
      return (
        <PropertyGroup
          bearerToken={props.bearerToken}
          refreshBearerToken={props.refreshBearerToken}
          selectedDatabase={props.selectedDatabase}
          propertyGroup={selectedPropertyGroup}
          update={updatePropertyGroup}
          close={() => props.changeSelectedPropertyGroup(null)}
          propertyRoles={propertyRoles}
          users={users}
          history={props.history}
        />
      );
    } else {
      return (
        <Fragment>
          <Button
            variant='outline-success'
            className='mb-2'
            onClick={() => {
              setShowAddPropertyGroupModal(true);
            }}
          >
            Add Property Group
          </Button>
          <Modal
            backdrop='static'
            show={showAddPropertyGroupModal}
            onHide={() => {
              setShowAddPropertyGroupModal(false);
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Add Property Group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Formik
                initialValues={{
                  name: '',
                  group_type: 'manual',
                  description: '',
                  add_staff: false
                }}
                validate={PropertyGroupFormValidator}
                onSubmit={(values, actions) => {
                  fetch(
                    `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
                    {
                      method: 'POST',
                      headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${props.bearerToken}`,
                        SeasonalDatabase: props.selectedDatabase
                      },
                      body: JSON.stringify(values)
                    }
                  )
                    .then((res) => {
                      if (res.ok)
                        res.json().then((data) => {
                          if (data) {
                            addPropertyGroup(data);
                            setShowAddPropertyGroupModal(false);
                            props.changeSelectedPropertyGroup(data.id);
                          }
                        });
                      if (res.status === 422)
                        res.json().then((data) => {
                          if (data.errors) {
                            Object.keys(data.errors).forEach((key) => {
                              actions.setFieldError(key, data.errors[key][0]);
                            });

                            actions.setSubmitting(false);
                          }
                        });
                      else if (res.status === 401) props.refreshBearerToken();
                      else throw Error(res.statusText);
                    })
                    .catch((err) => {
                      if (process.env.NODE_ENV === 'development')
                        console.error(err);
                      actions.setSubmitting(false);
                    });
                }}
              >
                {(props) => (
                  <Fragment>
                    <PropertyGroupForm {...props} />
                    <Button
                      varient='primary'
                      type='button'
                      onClick={props.handleSubmit}
                    >
                      Submit
                    </Button>
                    {props.isSubmitting ? <LoadingOverlay /> : null}
                  </Fragment>
                )}
              </Formik>
            </Modal.Body>
          </Modal>

          <PropertyGroupsTable
            data={propertyGroups.filter(
              (propertyGroup) => propertyGroup.group_type === 'manual'
            )}
            deletePropertyGroup={deletePropertyGroup}
            changeSelectedPropertyGroup={props.changeSelectedPropertyGroup}
          />
        </Fragment>
      );
    }
  }
}

PropertyGroups.propTypes = {
  // Authentication
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  selectedDatabase: PropTypes.string.isRequired,

  // Property Group
  selectedPropertyGroupId: PropTypes.number.isRequired,
  changeSelectedPropertyGroup: PropTypes.func.isRequired,

  // History
  history: PropTypes.object.isRequired
};

export default PropertyGroups;
